import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, ArrowRight, Clock, X, Package, CreditCard, 
  Receipt, FileText, Zap, CheckCircle, DollarSign, Percent,
  ChevronDown, ChevronUp, ChevronLeft, Sparkles, MessageCircle, MessageSquare,
  Video, Share2, TrendingUp, MapPin, Link2, PenTool, ChevronRight, Loader2, Copy
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api } from '../lib/auth';
import { cn, formatAIResponse } from '../lib/utils';
import { LogoIcon } from '../components/Logo';
import { GlowLine, GridPattern, FloatingParticles } from '../components/BackgroundEffects';
import { toast } from 'sonner';
import ConversationalOrderWidget from '../components/ConversationalOrderWidget';

// Full Managed Services Data with Plans (from CinematicAdsPage)
const MANAGED_SERVICES = [
  {
    id: 'meta-ads',
    name: 'Meta Ads',
    tagline: 'Reach more customers online',
    icon: Share2,
    color: 'from-blue-500 to-blue-600',
    image: '/images/meta-ads-team.png',
    description: 'Facebook & Instagram advertising managed by experts',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Monthly Ad Budget: $0 - $2,500',
          '3 Creatives / Month',
          'Pixel & Conversion Setup',
          '1 Audience',
          'Lookalike Audience',
          'Retargeting',
          'Custom Audience',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Monthly Ad Budget: $2,500 - $5,000',
          '6 Creatives / Month',
          'Pixel & Conversion Setup',
          '3 Audiences',
          'Lookalike Audience',
          'Retargeting',
          'Custom Audience',
          'Bi-Weekly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Monthly Ad Budget: $5,000+',
          '9 Creatives / Month',
          'Pixel & Conversion Setup',
          '5 Audiences',
          'Lookalike Audience',
          'Retargeting',
          'Custom Audience',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'What platforms are included?', a: 'Facebook and Instagram advertising through Meta Business Suite.' },
      { q: 'How are creatives developed?', a: 'Our team creates custom images and videos tailored to your brand.' },
    ]
  },
  {
    id: 'google-ads',
    name: 'Google Ads',
    tagline: 'Show up for high-intent searches',
    icon: Search,
    color: 'from-red-500 to-orange-500',
    image: '/images/google-ads-team.png',
    description: 'Capture customers actively searching for your services',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Monthly Ad Budget: $0 - $2,500',
          'Search Campaigns',
          'Display Campaigns',
          'Keyword Research',
          'Ad Extensions',
          'Conversion Tracking',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Monthly Ad Budget: $2,500 - $5,000',
          'Search Campaigns',
          'Display Campaigns',
          'Performance Max',
          'Advanced Keyword Research',
          'Ad Extensions',
          'Conversion Tracking',
          'Bi-Weekly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Monthly Ad Budget: $5,000+',
          'All Campaign Types',
          'YouTube Ads',
          'Performance Max',
          'Advanced Keyword Research',
          'All Ad Extensions',
          'Full Conversion Tracking',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'What campaign types are available?', a: 'Search, Display, Performance Max, and YouTube depending on your plan.' },
    ]
  },
  {
    id: 'tiktok-ads',
    name: 'TikTok Ads',
    tagline: 'Get traffic with short-form videos',
    icon: Video,
    color: 'from-pink-500 to-purple-500',
    image: '/images/tiktok-ads-team.png',
    description: 'Reach younger demographics with engaging video content',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Monthly Ad Budget: $0 - $2,500',
          'In-Feed Ads',
          '3 Video Creatives',
          'Audience Targeting',
          'Pixel Setup',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Monthly Ad Budget: $2,500 - $5,000',
          'In-Feed Ads',
          'TopView Ads',
          '6 Video Creatives',
          'Advanced Targeting',
          'Pixel Setup',
          'Bi-Weekly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Monthly Ad Budget: $5,000+',
          'All Ad Formats',
          '9+ Video Creatives',
          'Spark Ads',
          'Advanced Targeting',
          'Full Analytics',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'What ad formats are available?', a: 'In-Feed, TopView, and Spark Ads depending on your plan.' },
    ]
  },
  {
    id: 'seo',
    name: 'SEO',
    tagline: 'Boost organic traffic to your site',
    icon: TrendingUp,
    color: 'from-green-500 to-emerald-500',
    image: '/images/seo-team.png',
    description: 'Long-term organic growth through search optimization',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'Technical SEO Audit',
          'On-Page Optimization',
          '5 Keywords Targeted',
          'Monthly Blog Post',
          'Basic Link Building',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Full Technical SEO',
          'On-Page Optimization',
          '15 Keywords Targeted',
          '2 Blog Posts / Month',
          'Quality Link Building',
          'Local SEO',
          'Bi-Weekly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Enterprise Technical SEO',
          'Full On-Page Optimization',
          '30+ Keywords Targeted',
          '4 Blog Posts / Month',
          'Premium Link Building',
          'Local SEO',
          'Schema Markup',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: [
      { q: 'How long until I see results?', a: 'SEO is a long-term strategy. Most clients see improvements within 3-6 months.' },
    ]
  },
  {
    id: 'gbp-ranker',
    name: 'GBP Ranker',
    tagline: 'Improve your local search visibility',
    icon: MapPin,
    color: 'from-yellow-500 to-amber-500',
    description: 'Dominate local search with Google Business Profile optimization',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: [
          'GBP Optimization',
          'Weekly Posts',
          'Review Management',
          'Photo Optimization',
          'Monthly Reporting',
        ]
      },
      {
        name: 'PLUS',
        highlight: true,
        features: [
          'Full GBP Optimization',
          'Daily Posts',
          'Review Generation',
          'Q&A Management',
          'Photo & Video',
          'Bi-Weekly Reporting',
        ]
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: [
          'Multi-Location GBP',
          'Daily Posts',
          'Review Generation',
          'Full Q&A Management',
          'Premium Media',
          'Local Citations',
          'Weekly Reporting',
        ]
      }
    ],
    faqs: []
  },
  {
    id: 'backlinks',
    name: 'Backlinks',
    tagline: 'Increase your site authority',
    icon: Link2,
    color: 'from-purple-500 to-violet-500',
    description: 'Build domain authority with quality backlinks',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: ['5 Backlinks / Month', 'DA 30+ Sites', 'Niche Relevant', 'Monthly Report']
      },
      {
        name: 'PLUS',
        highlight: true,
        features: ['15 Backlinks / Month', 'DA 40+ Sites', 'Niche Relevant', 'Guest Posts', 'Bi-Weekly Report']
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: ['30 Backlinks / Month', 'DA 50+ Sites', 'Premium Guest Posts', 'HARO Links', 'Weekly Report']
      }
    ],
    faqs: []
  },
  {
    id: 'content',
    name: 'Content',
    tagline: 'Fuel SEO with optimized content',
    icon: PenTool,
    color: 'from-cyan-500 to-blue-500',
    description: 'SEO-optimized content that ranks and converts',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: ['2 Blog Posts / Month', '1000+ Words Each', 'SEO Optimized', 'Monthly Report']
      },
      {
        name: 'PLUS',
        highlight: true,
        features: ['4 Blog Posts / Month', '1500+ Words Each', 'SEO Optimized', 'Internal Linking', 'Bi-Weekly Report']
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: ['8 Blog Posts / Month', '2000+ Words Each', 'Pillar Content', 'Content Strategy', 'Weekly Report']
      }
    ],
    faqs: []
  },
  {
    id: 'social-posts',
    name: 'Social Posts',
    tagline: 'Stay consistent on social media',
    icon: MessageCircle,
    color: 'from-indigo-500 to-purple-500',
    description: 'Consistent social media presence across platforms',
    plans: [
      {
        name: 'PRO',
        highlight: false,
        features: ['12 Posts / Month', '3 Platforms', 'Content Calendar', 'Monthly Report']
      },
      {
        name: 'PLUS',
        highlight: true,
        features: ['20 Posts / Month', '4 Platforms', 'Content Calendar', 'Story Posts', 'Bi-Weekly Report']
      },
      {
        name: 'PLATINUM',
        highlight: false,
        features: ['30 Posts / Month', '5 Platforms', 'Content Calendar', 'Stories + Reels', 'Community Management', 'Weekly Report']
      }
    ],
    faqs: []
  },
];

// ServiceCard Component
const ServiceCard = ({ service, onSelect, hasImage }) => {
  const Icon = service.icon;
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -5 }}
      className="group cursor-pointer h-full"
      onClick={() => onSelect(service)}
    >
      <Card className="glass border-white/10 hover:border-primary/50 transition-all h-full overflow-hidden">
        {hasImage && service.image ? (
          <>
            <div className="relative h-32 overflow-hidden">
              <img 
                src={service.image} 
                alt={service.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            </div>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={cn("w-8 h-8 rounded-lg bg-gradient-to-br flex items-center justify-center", service.color)}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-heading font-bold text-white">{service.name}</h3>
              </div>
              <p className="text-primary text-sm mb-2">{service.tagline}</p>
              <div className="flex items-center text-primary text-sm font-medium group-hover:gap-2 transition-all">
                View Plans <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </>
        ) : (
          <>
            <div className={cn("h-2 w-full bg-gradient-to-r", service.color)} />
            <CardContent className="p-4">
              <div className={cn("w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center mb-3", service.color)}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-heading font-bold text-white mb-1">{service.name}</h3>
              <p className="text-primary text-xs mb-2">{service.tagline}</p>
              <div className="flex items-center text-primary text-xs font-medium group-hover:gap-1 transition-all">
                View Plans <ChevronRight className="w-3 h-3 ml-1" />
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </motion.div>
  );
};

// Service Detail Modal
const ServiceDetailModal = ({ service, onClose, onOrder, isOrdering }) => {
  const [activeTab, setActiveTab] = useState('plans');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [orderingPlan, setOrderingPlan] = useState(null);
  const Icon = service.icon;

  const handleOrder = (plan) => {
    setOrderingPlan(plan.name);
    onOrder(service, plan);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 pb-8 px-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={!isOrdering ? onClose : undefined}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-4xl bg-card rounded-2xl border border-white/10 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn("p-6 bg-gradient-to-r relative", service.color)}>
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-white/70 hover:text-white"
            disabled={isOrdering}
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Icon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-heading font-bold text-white">{service.name}</h2>
              <p className="text-white/80">{service.tagline}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="plans">Plans & Pricing</TabsTrigger>
              <TabsTrigger value="faqs">FAQs</TabsTrigger>
            </TabsList>

            <TabsContent value="plans">
              <div className="grid md:grid-cols-3 gap-4">
                {service.plans.map((plan) => (
                  <Card key={plan.name} className={cn(
                    "relative overflow-hidden",
                    plan.highlight ? "border-primary ring-2 ring-primary/20" : "border-white/10"
                  )}>
                    {plan.highlight && (
                      <div className="absolute top-0 left-0 right-0 bg-primary text-black text-xs font-bold text-center py-1">
                        MOST POPULAR
                      </div>
                    )}
                    <CardHeader className={plan.highlight ? "pt-8" : ""}>
                      <CardTitle className="text-lg">{plan.name}</CardTitle>
                      <p className="text-gray-400 text-sm">Contact for Quote</p>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                            <span className="text-gray-300">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className={cn("w-full mt-4", plan.highlight ? "btn-primary" : "")}
                        onClick={() => handleOrder(plan)}
                        disabled={isOrdering}
                      >
                        {isOrdering && orderingPlan === plan.name ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating Order...
                          </>
                        ) : (
                          <>Order {plan.name}</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="faqs">
              {service.faqs && service.faqs.length > 0 ? (
                <div className="space-y-3">
                  {service.faqs.map((faq, idx) => (
                    <div key={idx} className="glass rounded-lg overflow-hidden">
                      <button
                        className="w-full p-4 text-left flex items-center justify-between"
                        onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      >
                        <span className="font-medium text-white">{faq.q}</span>
                        <ChevronDown className={cn("w-5 h-5 text-gray-400 transition-transform", expandedFaq === idx && "rotate-180")} />
                      </button>
                      {expandedFaq === idx && (
                        <div className="px-4 pb-4 text-gray-400">{faq.a}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">No FAQs available for this service.</p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Info sections data
const COOP_COVERED_SERVICES = [
  'Websites & landing pages',
  'Funnels, automations & workflows',
  'CRM setup, configuration & support',
  'SEO, content, and digital marketing services',
  'Advertising strategy, setup & management',
  'AI agents, voice bots & AI support systems',
  'Design, copywriting & digital creative services'
];

const THIRD_PARTY_EXAMPLES = [
  { name: 'Advertising Spend', description: 'Meta, Google, TikTok, TV, Radio, etc.', note: 'Media spend billed at cost + 15% agency fee' },
  { name: 'Postcard Campaigns', description: 'Design & setup: 100% Co-Op covered', note: 'Printing & postage: billed at cost + 15% agency fee' },
  { name: 'Purchased Lead Lists', description: 'Billed at wholesale cost + 15% agency fee', note: '' },
  { name: 'Physical Products', description: 'Design work: 100% Co-Op covered', note: 'Manufacturing, materials & shipping: billed at cost + 15% agency fee' }
];

const ORDER_STEPS = [
  { step: 1, title: 'Place Your Order', description: 'Submit your request using our guided POD order builder.' },
  { step: 2, title: 'POD Review', description: 'Our team reviews scope, eligibility, timeline, and dependencies.' },
  { step: 3, title: 'On-Demand Proposal Issued', description: 'Your proposal clearly shows total value, Co-Op Funds, fees, and any third-party costs.' },
  { step: 4, title: 'Approve or Request Changes', description: 'You may approve, negotiate, or request adjustments.' },
  { step: 5, title: 'Accept & Activate', description: 'Once approved and the required fees are paid, production begins immediately.' }
];

export default function MarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedDept, setSelectedDept] = useState(searchParams.get('department') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isOrdering, setIsOrdering] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle ordering a managed service
  const handleOrderService = async (service, plan) => {
    setIsOrdering(true);
    try {
      const orderData = {
        service_name: `${service.name} - ${plan.name}`,
        service_type: 'managed',
        description: `${service.name} ${plan.name} Plan\n\nFeatures:\n${plan.features.map(f => `• ${f}`).join('\n')}`,
        requirements: `Please set up ${service.name} ${plan.name} plan for my business.`,
      };
      
      const response = await api.createOrder(orderData);
      toast.success('Order created successfully!');
      setSelectedService(null);
      navigate(`/orders/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error('Failed to create order. Please try again.');
    } finally {
      setIsOrdering(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (selectedDept) params.department_id = selectedDept;
    
    loadServices(params);
  }, [search, selectedDept]);

  const loadData = async () => {
    try {
      const [deptsRes, servicesRes] = await Promise.all([
        api.getDepartments(),
        api.getServices()
      ]);
      setDepartments(deptsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async (params) => {
    try {
      const response = await api.getServices(params);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    setSearchParams(prev => {
      if (value) prev.set('search', value);
      else prev.delete('search');
      return prev;
    });
  };

  const handleDeptSelect = (deptId) => {
    setSelectedDept(deptId === selectedDept ? '' : deptId);
    setSearchParams(prev => {
      if (deptId && deptId !== selectedDept) prev.set('department', deptId);
      else prev.delete('department');
      return prev;
    });
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Master Order Widget - Conversational Form (FIRST SECTION) */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-8"
          id="order-widget"
        >
          <div className="mb-4">
            <h3 className="font-heading text-xl font-semibold text-white flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              Quick Order - Submit a Task Now
            </h3>
            <p className="text-sm text-gray-400">Chat with our AI assistant to place an order in minutes</p>
          </div>
          <div className="flex justify-center lg:justify-start">
            <ConversationalOrderWidget 
              onOrderCreated={(order) => {
                toast.success('Order created! Redirecting to orders...');
                setTimeout(() => navigate('/orders'), 2000);
              }}
            />
          </div>
        </motion.div>

        {/* Explore & Place Unlimited Orders */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="glass rounded-2xl p-6 border border-white/10 relative overflow-hidden">
            <GridPattern className="opacity-30" />
            <div className="relative z-10">
              <h2 className="font-heading text-2xl font-bold text-white mb-2">
                Explore & Place Unlimited Orders
              </h2>
              <p className="text-gray-300 mb-4 max-w-3xl">
                When you submit an order through our Virtual Team POD, you unlock the full force of <span className="text-primary font-semibold">40+ elite departments</span>, each powered by AI, cinematic creativity, and industry-leading specialists ready to deliver world-class work on demand.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => scrollToSection('departments-grid')} className="btn-primary">
                  Browse Departments <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
                <Link to="/expert-session">
                  <Button variant="outline" className="border-secondary/30 text-secondary hover:bg-secondary/10">
                    <Video className="w-4 h-4 mr-2" /> Book Expert Session
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Managed Services Grid */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14 }}
          className="mb-8"
        >
          <div className="mb-4">
            <h3 className="font-heading text-lg font-semibold text-white">Managed Services</h3>
          </div>
          
          {/* Top Row - First 4 Services with Images */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {MANAGED_SERVICES.slice(0, 4).map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 + idx * 0.05 }}
              >
                <ServiceCard 
                  service={service} 
                  onSelect={setSelectedService}
                  hasImage={true}
                />
              </motion.div>
            ))}
          </div>
          
          {/* Bottom Row - Last 4 Services with Icons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {MANAGED_SERVICES.slice(4).map((service, idx) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25 + idx * 0.03 }}
              >
                <ServiceCard 
                  service={service} 
                  onSelect={setSelectedService}
                  hasImage={false}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* How It Works Accordion */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
        >
          <button
            onClick={() => setShowHowItWorks(!showHowItWorks)}
            className="w-full glass rounded-2xl p-6 text-left hover:border-primary/30 transition-colors"
            data-testid="how-it-works-toggle"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Package className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-white">
                    How to Place Unlimited Done For You Orders
                  </h2>
                  <p className="text-sm text-gray-400">
                    Place unlimited orders with your POD Departments
                  </p>
                </div>
              </div>
              {showHowItWorks ? (
                <ChevronUp className="w-6 h-6 text-primary" />
              ) : (
                <ChevronDown className="w-6 h-6 text-gray-400" />
              )}
            </div>
          </button>

          {showHowItWorks && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 space-y-6"
            >
              {/* Section 1: Service Fees Based on Plan */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Percent className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white">
                    1. Service Fees Based on Your Plan
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  For orders funded with your <span className="text-green-400 font-semibold">Co-Op Balance</span>, you only pay a small service fee based on your current plan:
                </p>
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-center">
                    <p className="text-3xl font-bold text-green-400">15%</p>
                    <p className="text-sm text-gray-400 mt-1">Partner Plan</p>
                  </div>
                  <div className="p-4 rounded-xl bg-primary/10 border border-primary/30 text-center">
                    <p className="text-3xl font-bold text-primary">Custom</p>
                    <p className="text-sm text-gray-400 mt-1">Enterprise</p>
                  </div>
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-center">
                    <p className="text-3xl font-bold text-yellow-400">Negotiable</p>
                    <p className="text-sm text-gray-400 mt-1">No Co-Op</p>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <p className="text-sm text-green-200">
                    <span className="font-bold">100% Covered by Co-Op:</span> The actual cost of your DFY job is covered entirely by your Co-Op Funds. You only pay the service fee!
                  </p>
                </div>
              </div>

              {/* Section 2: On Demand Pricing */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-secondary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white">
                    2. On Demand Pricing & Negotiation
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  Once your DFY job is accepted, our department team will generate an <span className="text-secondary font-semibold">On Demand Pricing proposal</span> for you.
                </p>
                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-300">Pay a small fee or <span className="text-white font-medium">negotiate a better deal</span> through private one-on-one conversations with your department team member</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-300">Not happy with the offer? <span className="text-white font-medium">Just let us know!</span> We&apos;ll work to make a better offer that works for you</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-300">Remember: <span className="text-green-400 font-semibold">The job cost is 100% covered by your Co-Op</span> — you just pay the DFY service fee</p>
                  </div>
                </div>
              </div>

              {/* Section 3: No Co-Op? No Problem */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-yellow-400" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white">
                    3. No Co-Op Funds? No Worries!
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  Don&apos;t have Co-Op funds yet? You can still place DFY orders!
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-300">Get a <span className="text-white font-medium">fair, discounted price</span> on any DFY service</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-300"><span className="text-white font-medium">Negotiate directly</span> with our department team for the best deal</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-300">You&apos;ll pay the full negotiated price (no Co-Op coverage applies)</p>
                  </div>
                </div>
                <div className="mt-4 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-sm text-yellow-200">
                    <span className="font-bold">Pro Tip:</span> Check out the <Link to="/billing" className="text-yellow-400 underline">Billing & Co-Op Balance</Link> page to see how you can unlock Co-Op funding!
                  </p>
                </div>
              </div>

              {/* Section 4: What Co-Op Funds Cover */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white">
                    4. What Your Co-Op Funds Cover
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  All Co-Op eligible Workspace services are covered <span className="text-green-400 font-semibold">100% by your Co-Op Funds</span>.
                </p>
                <p className="text-sm text-gray-400 mb-3">This includes (but is not limited to):</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {COOP_COVERED_SERVICES.map((service, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm text-gray-300">{service}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <p className="text-sm text-green-200">
                    Your available Co-Op Funds balance (up to <span className="font-bold">$120,000</span>) is always visible in your <Link to="/billing" className="text-green-400 underline">Billing & Co-Op Balance</Link> section.
                  </p>
                </div>
              </div>

              {/* Section 5: Third-Party Costs */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-yellow-400" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white">
                    5. Third-Party Costs + Media Management Fee
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  Some orders include third-party costs that are not eligible for Co-Op coverage. You pay the <span className="text-white font-medium">wholesale media cost</span> plus a reduced <span className="text-yellow-400 font-semibold">Media Management Fee</span> based on your plan:
                </p>
                
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-center">
                    <p className="text-3xl font-bold text-green-400">15%</p>
                    <p className="text-sm text-gray-400 mt-1">Partner Plan</p>
                  </div>
                  <div className="p-4 rounded-xl bg-primary/10 border border-primary/30 text-center">
                    <p className="text-3xl font-bold text-primary">Custom</p>
                    <p className="text-sm text-gray-400 mt-1">Enterprise</p>
                  </div>
                  <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-center">
                    <p className="text-3xl font-bold text-yellow-400">Negotiable</p>
                    <p className="text-sm text-gray-400 mt-1">No Co-Op</p>
                  </div>
                </div>

                <p className="text-sm text-gray-400 mb-3">This fee applies only to:</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                    Ad platforms (Meta, Google, TikTok, etc.)
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                    Postage & shipping costs
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                    Lead vendors & data providers
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                    Other third-party services
                  </li>
                </ul>

                <h4 className="text-sm font-semibold text-white mb-3">Common Examples</h4>
                <div className="space-y-3">
                  {THIRD_PARTY_EXAMPLES.map((example, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white/5">
                      <p className="text-white font-medium text-sm">{example.name}</p>
                      <p className="text-gray-400 text-xs mt-1">{example.description}</p>
                      {example.note && <p className="text-yellow-400/80 text-xs mt-1">{example.note}</p>}
                    </div>
                  ))}
                </div>

                <div className="mt-4 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                  <p className="text-sm text-green-200">
                    There are <span className="font-semibold">no hidden markups</span> — you pay wholesale cost plus your plan&apos;s Media Management Fee, all clearly itemized before approval.
                  </p>
                </div>
              </div>

              {/* Section 6: Summary */}
              <div className="glass rounded-2xl p-6 border border-primary/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-white">
                    6. Simple. Transparent. Unlimited Orders.
                  </h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-gray-300">Place unlimited DFY orders</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-gray-300">Negotiate any deal directly</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-gray-300">No surprise charges</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-gray-300">Full visibility into your Co-Op balance</span>
                  </div>
                </div>
                <p className="text-gray-300 mb-4">
                  Your AI Horse-Powered POD is built to help you <span className="text-primary font-semibold">move faster</span>, <span className="text-accent font-semibold">reduce cash outlay</span>, and <span className="text-green-400 font-semibold">scale with confidence</span>.
                </p>
                <p className="text-sm text-gray-400">
                  Your AI Concierge and Support Team are always available if you have questions before placing an order.
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>

        <GlowLine className="my-8" />

        {/* Search & Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-2xl p-4 mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                placeholder="Search services..."
                className="pl-10 input-dark"
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
                data-testid="marketplace-search"
              />
            </div>
            <Button 
              variant="outline" 
              className="btn-secondary sm:w-auto"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Departments
              {selectedDept && (
                <span className="ml-2 px-2 py-0.5 bg-primary/20 text-primary rounded-full text-xs">1</span>
              )}
            </Button>
          </div>

          {/* Department Filter */}
          {showFilters && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t border-white/10"
            >
              <div className="flex flex-wrap gap-2">
                {selectedDept && (
                  <button
                    onClick={() => handleDeptSelect('')}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary/20 text-primary text-sm"
                  >
                    Clear <X className="w-3 h-3" />
                  </button>
                )}
                {departments.map((dept) => (
                  <button
                    key={dept.id}
                    onClick={() => handleDeptSelect(dept.id)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm transition-colors",
                      selectedDept === dept.id
                        ? "bg-primary text-black"
                        : "bg-white/5 text-gray-300 hover:bg-white/10"
                    )}
                    data-testid={`dept-filter-${dept.key}`}
                  >
                    {dept.name}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Departments Grid (when no search/filter) */}
        {!search && !selectedDept && (
          <motion.div 
            id="departments-grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="mb-12"
          >
            {/* POD Departments Visual Banner with Parallax & Glow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-6 relative rounded-2xl overflow-hidden group"
            >
              {/* Animated glow border */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 via-purple-500 to-cyan-500 rounded-2xl opacity-0 group-hover:opacity-60 blur-sm transition-opacity duration-700 animate-pulse" style={{ animationDuration: '3s' }} />
              
              {/* Image container with parallax */}
              <motion.div
                className="relative rounded-2xl overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.4 }}
              >
                <motion.img 
                  src="/images/virtual-team-pod.png"
                  alt="40+ POD Departments Network"
                  className="w-full h-64 sm:h-80 object-cover rounded-2xl transform transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Animated scan line effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ animation: 'scanline 2s ease-in-out infinite' }} />
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent rounded-2xl" />
                
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-3">
                    <motion.div
                      animate={{ 
                        boxShadow: ['0 0 20px rgba(0,255,255,0.3)', '0 0 40px rgba(0,255,255,0.6)', '0 0 20px rgba(0,255,255,0.3)']
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="rounded-xl"
                    >
                      <LogoIcon size="md" />
                    </motion.div>
                    <div>
                      <h3 className="font-heading text-xl sm:text-2xl font-bold text-white">
                        40+ Virtual Departments
                      </h3>
                      <p className="text-sm text-gray-300">
                        AI-powered specialists ready to deliver world-class work on demand
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <h2 className="font-heading text-xl font-semibold text-white mb-2">Explore Our Departments</h2>
            <p className="text-gray-400 mb-6 text-sm">Discover what's possible! Click any department to explore services and get inspired.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <div key={i} className="h-32 bg-white/5 rounded-xl animate-pulse" />
                ))
              ) : (
                departments.map((dept, i) => (
                  <motion.button
                    key={dept.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.5) }}
                    onClick={() => handleDeptSelect(dept.id)}
                    className="glass rounded-xl p-5 text-left hover:border-cyan-500/40 hover:bg-cyan-500/5 transition-all group cursor-pointer"
                    data-testid={`dept-card-${dept.key}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-colors">
                        <Sparkles className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-white group-hover:text-cyan-400 transition-colors">
                          {dept.name}
                        </h3>
                        <p className="text-xs text-gray-500">{dept.service_count} services</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs px-2 py-1 rounded-full bg-accent/20 text-accent">Co-Op Eligible</span>
                      <span className="text-xs text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        Explore <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </motion.button>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Services Grid */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {/* Back Button when viewing a department */}
          {selectedDept && (
            <motion.button
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => { setSelectedDept(''); setSearchParams(prev => { prev.delete('department'); return prev; }); }}
              className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back to All Departments</span>
            </motion.button>
          )}

          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold text-white">
              {selectedDept 
                ? departments.find(d => d.id === selectedDept)?.name || 'Services'
                : search 
                  ? `Results for "${search}"`
                  : 'Featured Services'
              }
            </h2>
            <span className="text-sm text-gray-400">{services.length} services</span>
          </div>

          {/* Department Order Card - Shows when viewing a specific department */}
          {selectedDept && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
            >
              {(() => {
                const dept = departments.find(d => d.id === selectedDept);
                if (!dept) return null;
                return (
                  <div className="glass rounded-2xl p-6 border border-cyan-500/30 bg-gradient-to-r from-cyan-500/5 to-blue-500/5">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/30 to-blue-500/30 flex items-center justify-center">
                        <Sparkles className="w-7 h-7 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-semibold text-white">
                          {dept.name}
                        </h3>
                        <p className="text-sm text-gray-400">
                          Explore the services below to see what's possible. Ready to order? Head to the Quick Order section above!
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          )}

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-white/5 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : services.length === 0 && !selectedDept ? (
            <div className="text-center py-16">
              <LogoIcon size="lg" className="mx-auto mb-4 opacity-50" />
              <p className="text-gray-400 mb-4">No services found</p>
              <Button onClick={() => { setSearch(''); setSelectedDept(''); }} className="btn-secondary">
                Clear filters
              </Button>
            </div>
          ) : services.length === 0 && selectedDept ? (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-2">No specific services listed yet for this department.</p>
              <p className="text-sm text-gray-500">Use the "Request Quote" button above to place a custom order!</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, i) => {
                // Generate inspiring first-person description
                const getInspiringDescription = (svc) => {
                  const deptName = svc.department_name?.toLowerCase() || '';
                  const title = svc.title?.toLowerCase() || '';
                  
                  // Custom inspiring descriptions based on service type
                  if (deptName.includes('brand') || title.includes('logo') || title.includes('brand')) {
                    return "I need a powerful brand identity so I can stand out in my market, which will help me attract premium clients and build lasting recognition.";
                  } else if (deptName.includes('video') || title.includes('video')) {
                    return "I need professional video content so I can capture attention and tell my story, which will help me connect emotionally with my audience and drive more engagement.";
                  } else if (deptName.includes('website') || title.includes('website') || title.includes('landing')) {
                    return "I need a stunning website so I can make a powerful first impression, which will help me convert visitors into customers and grow my business 24/7.";
                  } else if (deptName.includes('seo') || title.includes('seo') || title.includes('search')) {
                    return "I need strong SEO so I can be found when people search for what I offer, which will help me get consistent organic traffic and leads without paying for every click.";
                  } else if (deptName.includes('social') || title.includes('social')) {
                    return "I need engaging social media content so I can build a loyal community, which will help me stay top-of-mind and turn followers into customers.";
                  } else if (deptName.includes('ad') || title.includes('ad') || deptName.includes('paid')) {
                    return "I need high-converting ads so I can reach my ideal customers at scale, which will help me grow revenue predictably and maximize my marketing ROI.";
                  } else if (deptName.includes('email') || title.includes('email')) {
                    return "I need effective email marketing so I can nurture leads and stay connected, which will help me turn subscribers into repeat customers.";
                  } else if (deptName.includes('graphic') || title.includes('design')) {
                    return "I need professional designs so I can look credible and polished, which will help me win trust and compete with bigger brands.";
                  } else if (deptName.includes('ai') || title.includes('ai') || title.includes('chatbot')) {
                    return "I need AI automation so I can scale my operations without adding headcount, which will help me serve more customers while reducing costs.";
                  } else if (deptName.includes('content') || title.includes('blog') || title.includes('copywriting')) {
                    return "I need compelling content so I can educate and engage my audience, which will help me establish authority and drive organic growth.";
                  } else if (deptName.includes('crm') || title.includes('crm')) {
                    return "I need a well-organized CRM so I can track every lead and customer, which will help me close more deals and never miss an opportunity.";
                  } else if (deptName.includes('reputation') || title.includes('review')) {
                    return "I need reputation management so I can build trust with social proof, which will help me convert more browsers into buyers.";
                  } else if (deptName.includes('funnel') || title.includes('funnel')) {
                    return "I need optimized funnels so I can guide prospects to purchase, which will help me increase conversions and maximize customer lifetime value.";
                  } else if (deptName.includes('podcast') || title.includes('podcast')) {
                    return "I need professional podcast production so I can share my expertise, which will help me build authority and reach new audiences.";
                  } else if (deptName.includes('print') || title.includes('print') || title.includes('merch')) {
                    return "I need quality print materials so I can make tangible impressions, which will help me stay memorable long after the first meeting.";
                  } else {
                    return "I need this service so I can level up my business, which will help me achieve my goals faster and more efficiently.";
                  }
                };
                
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    data-testid={`service-card-${service.id}`}
                  >
                    <div className="card-marketing h-full flex flex-col hover:border-cyan-500/30 transition-colors">
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <span className="px-2 py-1 rounded-md bg-white/5 text-xs text-gray-400">
                            {service.department_name}
                          </span>
                          {service.covered_by_coop && (
                            <span className="px-2 py-1 rounded-md bg-accent/20 text-xs text-accent">
                              Co-Op Eligible
                            </span>
                          )}
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-white mb-3">
                          {service.title}
                        </h3>
                        
                        {/* Inspiring first-person description */}
                        <p className="text-sm text-cyan-200/80 italic mb-4 leading-relaxed">
                          "{getInspiringDescription(service)}"
                        </p>
                        
                        {/* What you might need */}
                        <div className="space-y-2 mb-4">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">What this typically includes:</p>
                          <p className="text-sm text-gray-400">
                            {service.short_description || 'Professional execution tailored to your specific needs, delivered by our expert team.'}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Est. {service.sla_estimate_days} days
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-4 mt-4 border-t border-white/10">
                        <p className="text-xs text-gray-500 text-center">
                          Ready to get started? Use the <span className="text-cyan-400">Quick Order</span> above!
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Service Request CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 glass rounded-2xl p-8 text-center"
          >
            <LogoIcon size="lg" className="mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-white mb-2">
              Can&apos;t find what you need?
            </h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Request a custom service and our team will review it. We&apos;re always expanding our offerings!
            </p>
            <Link to="/service-requests">
              <Button className="btn-secondary" data-testid="request-service-cta">
                Request a Service <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Service Detail Modal */}
      <AnimatePresence>
        {selectedService && (
          <ServiceDetailModal
            service={selectedService}
            onClose={() => !isOrdering && setSelectedService(null)}
            onOrder={handleOrderService}
            isOrdering={isOrdering}
          />
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
}
